import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import AdminLayout from '../../../components/AdminLayout/AdminLayout';
import DashboardMainContainer from '../../../components/common/Containers/DashboardMainContainer';
import {
  AdminPageTitle,
  AdminPanelTitle,
  AdminDetailFieldLabel,
  AdminDetailFieldValue,
  AdminSupportingText,
  AdminTableBody,
  AdminHeaderAction,
  AdminFormSecondaryAction,
  AdminRowActionsButton,
  AdminLinkAction,
  AdminRowMenuItem,
  ADMIN_INLINE_ROW_ACTIONS_MENU_CLASS,
  AdminDetailTableHeader,
  adminFormInputClasses,
  adminFormTextareaClasses,
  ADMIN_DETAIL_MAIN_HEADER_CLASS,
  ADMIN_FORM_PANEL_CLASS,
  ADMIN_DETAIL_TABLE_CLASS,
} from '../shared/adminUx';

const ARTICLE_USE_OPTIONS = ['Answer Guide', 'Troubleshooting', 'Support Process'];
const CATEGORY_OPTIONS = [
  'Account Access',
  'Billing & Subscriptions',
  'Technical Help',
  'General Support',
  'Appeal',
  'Compliance Query',
];

const KB_ARTICLES = {
  'KB-0001': {
    articleId: 'KB-0001',
    title: 'Password reset email not received',
    category: 'Account Access',
    articleUse: 'Troubleshooting',
    lastUpdated: '05 Apr 2026',
    whenToUse:
      'Use this article when a customer says password reset emails are missing, delayed, expired, or not working as expected.',
    quickAnswer:
      'If a customer is not receiving the password reset email, first confirm the account email, ask them to check spam or junk folders, and then check whether reset email delivery is working normally before giving the next step.',
    guidanceSteps: [
      'Confirm the customer is using the correct account email.',
      'Ask whether the reset request was made more than once.',
      'Ask the customer to check spam, junk, and filtered folders.',
      'Check whether there is a current delivery issue affecting reset emails.',
      'If delivery failure is confirmed, update the customer before asking them to retry.',
      'Route the issue to the assigned team if it continues after the email checks are complete.',
    ],
    suggestedReply:
      'Hi, thanks for contacting support. Please confirm that you are using the correct email address for your account and check your spam or junk folder for the reset email. We are also checking whether there is a recent delivery issue affecting password reset messages. If needed, we will guide you through the next step.',
    relatedFiles: [
      { fileName: 'password-reset-checklist.pdf', fileType: 'PDF' },
      { fileName: 'reset-email-flow.png', fileType: 'Image' },
    ],
    articleHistory: [
      { version: 'v3', updatedBy: 'Support - ABC Name', updatedOn: '05 Apr 2026', summary: 'Added reset email delivery check guidance' },
      { version: 'v2', updatedBy: 'Development - ABC Name', updatedOn: '03 Apr 2026', summary: 'Updated suggested reply wording' },
      { version: 'v1', updatedBy: 'Support - ABC Name', updatedOn: '30 Mar 2026', summary: 'Initial article created' },
    ],
  },
};

const emptyForm = {
  title: '',
  category: '',
  articleUse: '',
  whenToUse: '',
  quickAnswer: '',
  guidanceStepsText: '',
  suggestedReply: '',
  lastUpdated: '—',
  relatedFiles: [],
  articleHistory: [],
};

const KnowledgeBaseDetail = () => {
  const { articleId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const isNew = !articleId || location.pathname.endsWith('/new');
  const isReadOnly = !isNew && new URLSearchParams(location.search).get('mode') === 'view';

  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState(emptyForm);
  const [relatedFileRowActionsOpen, setRelatedFileRowActionsOpen] = useState({});
  const relatedFileRowActionsRefs = useRef({});

  const toggleRelatedFileRowActions = (fileKey) => {
    setRelatedFileRowActionsOpen((prev) => {
      if (prev[fileKey]) return {};
      return { [fileKey]: true };
    });
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      const isOutsideAll = Object.values(relatedFileRowActionsRefs.current).every(
        (ref) => ref && !ref.contains(event.target)
      );
      if (isOutsideAll) setRelatedFileRowActionsOpen({});
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    setLoading(true);
    const article = isNew ? null : (KB_ARTICLES[articleId] || KB_ARTICLES['KB-0001']);

    if (article) {
      setFormData({
        title: article.title,
        category: article.category,
        articleUse: article.articleUse,
        whenToUse: article.whenToUse,
        quickAnswer: article.quickAnswer,
        guidanceStepsText: article.guidanceSteps.join('\n'),
        suggestedReply: article.suggestedReply,
        lastUpdated: article.lastUpdated,
        relatedFiles: article.relatedFiles,
        articleHistory: article.articleHistory,
      });
    } else {
      setFormData(emptyForm);
    }

    setLoading(false);
  }, [articleId, isNew]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const validate = () => {
    if (!formData.title || !formData.category || !formData.articleUse || !formData.whenToUse || !formData.quickAnswer || !formData.guidanceStepsText || !formData.suggestedReply) {
      alert('Please complete all required fields.');
      return false;
    }
    return true;
  };

  const handleDraft = () => {
    if (!validate()) return;
    alert('Draft saved');
    navigate('/admin/support/kb');
  };

  const handleSaveChanges = () => {
    if (!validate()) return;
    alert('Changes saved');
    navigate('/admin/support/kb');
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-96">
          <AdminSupportingText className="text-offblack">Loading article data...</AdminSupportingText>
        </div>
      </AdminLayout>
    );
  }

  const displayArticleId = isNew ? `KB-${Date.now().toString().slice(-4)}` : articleId;
  const showArticleDetailsAsText = !isNew;
  const fieldClass = adminFormInputClasses(false);
  const textareaClass = adminFormTextareaClasses(false);

  return (
    <AdminLayout>
      <div className="tp-container-main">
        <div>
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
          <div className="flex-1">
            <AdminPageTitle>
              {formData.title || 'Knowledge Article'}
            </AdminPageTitle>
          </div>
        </div>

        <DashboardMainContainer className={ADMIN_DETAIL_MAIN_HEADER_CLASS}>
          <AdminPanelTitle className="mb-4">Article Details</AdminPanelTitle>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {!isNew && (
              <div className="space-y-1">
                <AdminDetailFieldLabel>Article ID</AdminDetailFieldLabel>
                <AdminDetailFieldValue>{displayArticleId || '—'}</AdminDetailFieldValue>
              </div>
            )}
            <div className="space-y-1">
              <AdminDetailFieldLabel>{showArticleDetailsAsText ? 'Title' : 'Title *'}</AdminDetailFieldLabel>
              {showArticleDetailsAsText ? (
                <AdminDetailFieldValue>{formData.title || '—'}</AdminDetailFieldValue>
              ) : (
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  className={`${fieldClass} w-full`}
                />
              )}
            </div>
            <div className="space-y-1">
              <AdminDetailFieldLabel>{showArticleDetailsAsText ? 'Category' : 'Category *'}</AdminDetailFieldLabel>
              {showArticleDetailsAsText ? (
                <AdminDetailFieldValue>{formData.category || '—'}</AdminDetailFieldValue>
              ) : (
                <select
                  value={formData.category}
                  onChange={(e) => handleInputChange('category', e.target.value)}
                  className={`${fieldClass} w-full`}
                >
                  <option value="">Select category</option>
                  {CATEGORY_OPTIONS.map((cat) => (
                    <option key={cat} value={cat}>
                      {cat}
                    </option>
                  ))}
                </select>
              )}
            </div>
            <div className="space-y-1">
              <AdminDetailFieldLabel>{showArticleDetailsAsText ? 'Article Use' : 'Article Use *'}</AdminDetailFieldLabel>
              {showArticleDetailsAsText ? (
                <AdminDetailFieldValue>{formData.articleUse || '—'}</AdminDetailFieldValue>
              ) : (
                <select
                  value={formData.articleUse}
                  onChange={(e) => handleInputChange('articleUse', e.target.value)}
                  className={`${fieldClass} w-full`}
                >
                  <option value="">Select article use</option>
                  {ARTICLE_USE_OPTIONS.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              )}
            </div>
          </div>
        </DashboardMainContainer>

        <DashboardMainContainer className={ADMIN_FORM_PANEL_CLASS}>
          <AdminPanelTitle className="mb-4">When to Use</AdminPanelTitle>
          <textarea
            value={formData.whenToUse}
            onChange={(e) => handleInputChange('whenToUse', e.target.value)}
            disabled={isReadOnly}
            className={`${textareaClass} w-full resize-y min-h-[100px]`}
          />
        </DashboardMainContainer>

        <DashboardMainContainer className={ADMIN_FORM_PANEL_CLASS}>
          <AdminPanelTitle className="mb-4">Quick Answer</AdminPanelTitle>
          <textarea
            value={formData.quickAnswer}
            onChange={(e) => handleInputChange('quickAnswer', e.target.value)}
            disabled={isReadOnly}
            className={`${textareaClass} w-full resize-y min-h-[120px]`}
          />
        </DashboardMainContainer>

        <DashboardMainContainer className={ADMIN_FORM_PANEL_CLASS}>
          <AdminPanelTitle className="mb-4">Guidance Steps</AdminPanelTitle>
          <textarea
            value={formData.guidanceStepsText}
            onChange={(e) => handleInputChange('guidanceStepsText', e.target.value)}
            disabled={isReadOnly}
            className={`${textareaClass} w-full resize-y min-h-[160px]`}
            placeholder="One step per line"
          />
        </DashboardMainContainer>

        <DashboardMainContainer className={ADMIN_FORM_PANEL_CLASS}>
          <AdminPanelTitle className="mb-4">Suggested Reply</AdminPanelTitle>
          <textarea
            value={formData.suggestedReply}
            onChange={(e) => handleInputChange('suggestedReply', e.target.value)}
            disabled={isReadOnly}
            className={`${textareaClass} w-full resize-y min-h-[140px]`}
          />
        </DashboardMainContainer>

        <DashboardMainContainer className={ADMIN_DETAIL_TABLE_CLASS}>
          <AdminPanelTitle className="mb-4">Related Files</AdminPanelTitle>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-lightgray">
                  <th className="text-left py-3 px-4">
                    <AdminDetailTableHeader>File Name</AdminDetailTableHeader>
                  </th>
                  <th className="text-left py-3 px-4">
                    <AdminDetailTableHeader>File Type</AdminDetailTableHeader>
                  </th>
                  <th className="text-center py-3 px-4">
                    <AdminDetailTableHeader>Actions</AdminDetailTableHeader>
                  </th>
                </tr>
              </thead>
              <tbody>
                {formData.relatedFiles.length === 0 ? (
                  <tr>
                    <td colSpan={3} className="py-3 px-4">
                      <AdminSupportingText className="text-mediumgray">No related files</AdminSupportingText>
                    </td>
                  </tr>
                ) : (
                  formData.relatedFiles.map((file) => (
                    <tr key={file.fileName} className="border-b border-lightgray">
                      <td className="py-3 px-4">
                        <AdminTableBody>{file.fileName}</AdminTableBody>
                      </td>
                      <td className="py-3 px-4">
                        <AdminTableBody>{file.fileType}</AdminTableBody>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center justify-center relative">
                          <div
                            className="relative inline-flex"
                            ref={(el) => {
                              relatedFileRowActionsRefs.current[file.fileName] = el;
                            }}
                          >
                            <AdminRowActionsButton
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleRelatedFileRowActions(file.fileName);
                              }}
                              title="Actions"
                              aria-label="Row actions"
                            >
                              <i className="fi fi-rs-menu-dots-vertical text-offblack flex items-center justify-center" aria-hidden />
                            </AdminRowActionsButton>
                            {relatedFileRowActionsOpen[file.fileName] && (
                              <div className={ADMIN_INLINE_ROW_ACTIONS_MENU_CLASS}>
                                  <AdminRowMenuItem
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setRelatedFileRowActionsOpen({});
                                    }}
                                  >
                                    View
                                  </AdminRowMenuItem>
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </DashboardMainContainer>

        <DashboardMainContainer className={ADMIN_DETAIL_TABLE_CLASS}>
          <AdminPanelTitle className="mb-4">Article History</AdminPanelTitle>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-lightgray">
                  <th className="text-left py-3 px-4">
                    <AdminDetailTableHeader>Version</AdminDetailTableHeader>
                  </th>
                  <th className="text-left py-3 px-4">
                    <AdminDetailTableHeader>Updated By</AdminDetailTableHeader>
                  </th>
                  <th className="text-left py-3 px-4">
                    <AdminDetailTableHeader>Updated On</AdminDetailTableHeader>
                  </th>
                  <th className="text-left py-3 px-4">
                    <AdminDetailTableHeader>Summary</AdminDetailTableHeader>
                  </th>
                </tr>
              </thead>
              <tbody>
                {formData.articleHistory.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="py-3 px-4">
                      <AdminSupportingText className="text-mediumgray">No article history</AdminSupportingText>
                    </td>
                  </tr>
                ) : (
                  formData.articleHistory.map((row) => (
                    <tr key={`${row.version}-${row.updatedOn}`} className="border-b border-lightgray">
                      <td className="py-3 px-4">
                        <AdminTableBody>{row.version}</AdminTableBody>
                      </td>
                      <td className="py-3 px-4">
                        <AdminTableBody>{row.updatedBy}</AdminTableBody>
                      </td>
                      <td className="py-3 px-4">
                        <AdminTableBody>{row.updatedOn}</AdminTableBody>
                      </td>
                      <td className="py-3 px-4">
                        <AdminTableBody>{row.summary}</AdminTableBody>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </DashboardMainContainer>

        {!isReadOnly && (
          <div className="flex justify-end gap-3">
            <AdminFormSecondaryAction type="button" onClick={handleDraft}>
              Draft
            </AdminFormSecondaryAction>
            <AdminHeaderAction onClick={handleSaveChanges}>
              Save
            </AdminHeaderAction>
          </div>
        )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default KnowledgeBaseDetail;
